export interface InflationFormValues {
  currentMonthlyExpense: number;
  inflationRate: number;
  yearsToRetirement: number;
  postRetirementYears: number;
}

export interface InflationDataPoint {
  year: number;
  nominalExpense: number;
  realExpense: number;
}

export interface InflationResult {
  realMonthlyExpenseAtRetirement: number;
  nominalTotalNeeded: number;
  realTotalNeeded: number;
  inflationGap: number;
  purchasingPowerOf1M: number;
  yearlyData: InflationDataPoint[];
}

export function calculateInflation(values: InflationFormValues): InflationResult {
  const { currentMonthlyExpense, inflationRate, yearsToRetirement, postRetirementYears } = values;
  const r = inflationRate / 100;

  const realMonthlyExpenseAtRetirement = Math.round(
    currentMonthlyExpense * Math.pow(1 + r, yearsToRetirement)
  );

  const nominalTotalNeeded = currentMonthlyExpense * 12 * postRetirementYears;

  let realTotalNeeded: number;
  if (r === 0) {
    realTotalNeeded = realMonthlyExpenseAtRetirement * 12 * postRetirementYears;
  } else {
    realTotalNeeded = Math.round(
      realMonthlyExpenseAtRetirement *
        12 *
        ((Math.pow(1 + r, postRetirementYears) - 1) / r)
    );
  }

  const inflationGap = realTotalNeeded - nominalTotalNeeded;
  const purchasingPowerOf1M = Math.round(1_000_000 / Math.pow(1 + r, yearsToRetirement));

  const step = Math.max(1, Math.ceil(yearsToRetirement / 40));
  const yearlyData: InflationDataPoint[] = [];
  for (let year = 0; year <= yearsToRetirement; year += step) {
    yearlyData.push({
      year,
      nominalExpense: currentMonthlyExpense,
      realExpense: Math.round(currentMonthlyExpense * Math.pow(1 + r, year)),
    });
  }
  if (yearlyData[yearlyData.length - 1].year !== yearsToRetirement) {
    yearlyData.push({
      year: yearsToRetirement,
      nominalExpense: currentMonthlyExpense,
      realExpense: realMonthlyExpenseAtRetirement,
    });
  }

  return {
    realMonthlyExpenseAtRetirement,
    nominalTotalNeeded,
    realTotalNeeded,
    inflationGap,
    purchasingPowerOf1M,
    yearlyData,
  };
}

export function getInflationCallout(result: InflationResult, inflationRate: number) {
  const gapPct =
    result.nominalTotalNeeded > 0 ? (result.inflationGap / result.nominalTotalNeeded) * 100 : 0;

  if (inflationRate >= 4) {
    return {
      title: `연 ${inflationRate}% 물가 상승은 24년마다 구매력을 절반으로 줄입니다`,
      body: '높은 물가 상승률 환경에서는 현금·예금 보유가 가장 위험한 선택입니다. 물가 이상의 실질 수익을 내는 자산(주식 ETF, 물가연동채)을 포트폴리오에 포함하세요.',
      cta: { label: '투자가이드에서 인플레이션 헤지 전략 보기', href: '/guide' },
    };
  }

  if (gapPct > 30) {
    return {
      title: `물가 반영 시 ${Math.round(gapPct)}% 추가 자금이 필요합니다`,
      body: '명목 금액으로 목표를 세우면 실질 구매력이 크게 부족해집니다. 목표 자산에 물가 상승 버퍼를 반드시 포함해 재계산해 보세요.',
      cta: { label: '목표 설계 계산기에서 재계산', href: '/retirement/goal' },
    };
  }

  return {
    title: '물가를 고려한 계획이 진짜 노후 준비입니다',
    body: '명목 금액이 아닌 실질 구매력으로 목표를 설정하면 실제 생활 수준을 유지할 수 있습니다.',
    cta: { label: '목표 설계 계산기로 재설계', href: '/retirement/goal' },
  };
}
